import React from 'react';
import classNames from 'classnames';

interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SelectFieldProps {
  id: string;
  name: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  options: SelectOption[];
  placeholder?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  helpText?: string;
}

export const SelectField: React.FC<SelectFieldProps> = ({
  id,
  name,
  label,
  value,
  onChange,
  onBlur,
  options,
  placeholder = 'Please select...',
  error,
  required = false,
  disabled = false,
  className,
  helpText,
}) => {
  const hasError = Boolean(error);

  return (
    <div className={classNames('space-y-1', className)}>
      <label
        htmlFor={id}
        className={classNames(
          'block text-sm font-medium',
          hasError ? 'text-error-700' : 'text-gray-700'
        )}
      >
        {label}
        {required && <span className="text-error-500 ml-1">*</span>}
      </label>
      
      <select
        id={id}
        name={name}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        disabled={disabled}
        required={required}
        className={classNames(
          'form-input',
          hasError && 'error',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
        aria-invalid={hasError}
        aria-describedby={error ? `${id}-error` : helpText ? `${id}-help` : undefined}
      >
        <option value="" disabled>
          {placeholder}
        </option>
        {options.map((option) => (
          <option
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </option>
        ))}
      </select>
      
      {error && (
        <p id={`${id}-error`} className="text-sm text-error-600 animate-fade-in">
          {error}
        </p>
      )}
      
      {helpText && !error && (
        <p id={`${id}-help`} className="text-sm text-gray-500">
          {helpText}
        </p>
      )}
    </div>
  );
};
